import InternalLinks from 'components/home-page/internal-links';
import { TwoColumnLayout } from 'components/atomic/templates/two-column-layout';
import { OpenHouseCard, OPEN_HOUSE_CARD_VARIANTS } from 'ui/open-house';
import { ConnectionRequestSidebarSection } from 'sections/listing/connection-request-sidebar';
import {
  ListingHeaderSection,
  ListingActionButtonsSection,
  StickyNavWithActions,
} from 'sections';
import { useGalleryWithBadges } from 'sections/listing/gallery';
import { HeroGallerySwitch, HeroContentSwitch } from 'sections/listing/hero';
import { ExperimentalSection } from 'ui/experimental-section';
import { getTenantById } from '@tenants';
import { AllowedExperimentKey } from '@tenants/types';
import { useListingSections } from 'hooks/use-listing-sections';
import { useListingSectionVisibility } from '../hooks/use-listing-section-visibility';
import { useHeaderHeight } from 'hooks/use-header-height';
import { TAB_BAR_HEIGHT } from 'sections/listing/sticky-header-tabs';
import { useExperimentElementVisibility, ExperimentElement } from 'experiments/element-visibility';
import { hasFeatureFlag, FEATURE_FLAGS } from '../../types';
import { ContentSection } from '../sections/content';
import { SidebarSection } from '../sections/sidebar';
import SimilarListingsSections from '../../components/similar-listings-sections';
import { LegacyHeroLayout, NewHeroLayout } from './layout-variants';
import { prepareHeroProps, prepareEventHandlers } from './utils';
import { getSectionId, SECTION_IDS } from '../utils/get-section-id';
import type { AddressPageV2ViewProps } from '../address-page-v2-view';
import similarListingsStyles from './similar-listings-section.module.scss';
import layoutVariantStyles from './layout-variants.module.scss';

/**
 * Desktop Layout for Address Page V2
 * 
 * Layout structure:
 * 1. Breadcrumbs (always shown if enabled)
 * 2. Action Buttons or Sticky Nav (orchestrator-controlled based on experiments)
 * 3. Content Layout - depends on LISTING_HERO experiment:
 *    - LISTING_HERO control: Full-width gallery + Legacy layout with overlapping sidebar
 *    - LISTING_HERO variation: Max-width gallery + TwoColumnLayout without overlap
 * 4. Similar/Comparable Listings (max-width container)
 * 5. Internal Links (always shown)
 * 
 * Experiments:
 * - STICKY_HEADER_TABS: Shows sticky nav bar with tabs
 * - LISTING_HERO: Switches between legacy and new hero layouts
 * 
 * Element visibility is controlled by the orchestrator, eliminating nested
 * ExperimentalSection complexity.
 */
export function DesktopLayout({ data, handlers }: AddressPageV2ViewProps) {
  const {
    hasAccessRestriction,
    isBreadcrumbsSectionVisible,
    galleryData,
    headerData,
    complianceData,
    openHouseData,
    commonData,
  } = data;

  const { listing, internalLinks, flags, complianceRequirement, tenantId } = commonData;
  const { onImageClick, onComplianceClick, onSeeAllClick } = handlers;

  // Element visibility - controls visibility based on experiments and viewport
  const visibility = useExperimentElementVisibility();

  // Prepare data for child components
  const galleryDataWithBadges = useGalleryWithBadges(galleryData, tenantId, listing);
  const heroProps = prepareHeroProps(data, handlers);
  const eventHandlers = prepareEventHandlers(data, handlers);

  // Calculate visibility flags for listing sections (tabs)
  const visibilityFlags = useListingSectionVisibility(commonData);
  // Hide commute section in the sticky tab variation
  const sectionsVisibilityFlags = {
    ...visibilityFlags,
    shouldShowCommute: false,
  };
  const listingSections = useListingSections({ visibilityFlags: sectionsVisibilityFlags });

  // Calculate scroll offset for sticky tab navigation (header + tab bar + buffer)
  const headerHeight = useHeaderHeight();
  const scrollOffset = headerHeight + TAB_BAR_HEIGHT + 16;

  // Viewport-specific props for content section
  const tenant = getTenantById(tenantId);
  const isMapInteractive = true; // Desktop: maps are interactive
  const showMyListingAgent = 
    hasFeatureFlag(flags, FEATURE_FLAGS.MY_LINK_MY_LEAD_ENABLED) && 
    tenant.features.MyLinkMyLead;

  // Hero gallery element (to be passed to layout variants)
  const heroGallery = (
    <HeroGallerySwitch
      listing={listing}
      gallery={galleryDataWithBadges}
      blur={hasAccessRestriction}
      complianceData={complianceData}
      complianceRequirement={complianceRequirement}
      onImageClick={onImageClick}
      onSeeAllClick={onSeeAllClick}
      onComplianceClick={onComplianceClick}
      share={heroProps.share}
    />
  );

  const connectionRequestCtaSlot = (
    <ConnectionRequestSidebarSection
      onBookShowing={handlers.onBookShowing}
      onRequestInfo={handlers.onRequestInfo}
      listing={listing}
    />
  );

  // Main content to render (WITHOUT similar listings - those go after the layout)
  const mainContent = (
    <>
      <HeroContentSwitch {...heroProps} />
      {visibility.shouldShow(ExperimentElement.OPEN_HOUSE_CARD) && openHouseData.hasValue && (
        <OpenHouseCard data={openHouseData} variant={OPEN_HOUSE_CARD_VARIANTS.DESKTOP} />
      )}
      <ContentSection 
        data={commonData} 
        handlers={eventHandlers}
        isMapInteractive={isMapInteractive}
        showMyListingAgent={showMyListingAgent}
        viewport="desktop"
        priceHistoryRef={data.priceHistoryRef}
      />
    </>
  );

  const defaultHeroLayout = visibility.shouldShow(ExperimentElement.HERO_LAYOUT_V2) ? (
    <NewHeroLayout
      heroGallery={heroGallery}
      mainContent={mainContent}
      data={commonData}
      handlers={eventHandlers}
    />
  ) : (
    <LegacyHeroLayout
      heroGallery={heroGallery}
      mainContent={mainContent}
      data={commonData}
      handlers={eventHandlers}
    />
  );

  // Check if we should render similar listings sections
  // Similar listings are rendered outside the two-column layout as a separate full-width section
  // but still part of the scrollable content (accessible via sticky tabs)
  const hasAddressCoordinates = listing.position?.coordinates?.length === 2;
  const shouldShowSimilarListings = 
    (hasFeatureFlag(flags, FEATURE_FLAGS.SIMILAR_LISTINGS_ENABLED) || 
     hasFeatureFlag(flags, FEATURE_FLAGS.COMPARABLE_LISTINGS_ENABLED)) && 
    hasAddressCoordinates;

  // Similar listings element - rendered outside the two-column layout with section ID for tab navigation
  // Width matches the content layout: 1200px for legacy, 1400px for new hero
  const similarListingsSection = shouldShowSimilarListings ? (
    <ExperimentalSection
      experimentKey={AllowedExperimentKey.LISTING_HERO}
      control={
        // Legacy layout: 1200px max-width
        <section
          id={getSectionId(SECTION_IDS.SIMILAR_LISTINGS, 'desktop')}
          className={similarListingsStyles.similarListingsSection}
          style={{ '--scroll-offset': `${scrollOffset}px` } as React.CSSProperties}
        >
          <SimilarListingsSections
            listing={listing}
            showCarousel={false}
            hideSimilarListings={!hasFeatureFlag(flags, FEATURE_FLAGS.SIMILAR_LISTINGS_ENABLED)}
            hideComparableListings={!hasFeatureFlag(flags, FEATURE_FLAGS.COMPARABLE_LISTINGS_ENABLED)}
            onListingCardClick={handlers.onSimilarListingsCardClick}
          />
        </section>
      }
      variation={
        // New hero layout: 1400px max-width to match hero container
        <section 
          id={getSectionId(SECTION_IDS.SIMILAR_LISTINGS, 'desktop')}
          className={layoutVariantStyles.similarListingsContainer}
          style={{ '--scroll-offset': `${scrollOffset}px` } as React.CSSProperties}
        >
          <SimilarListingsSections
            listing={listing}
            showCarousel={false}
            hideSimilarListings={!hasFeatureFlag(flags, FEATURE_FLAGS.SIMILAR_LISTINGS_ENABLED)}
            hideComparableListings={!hasFeatureFlag(flags, FEATURE_FLAGS.COMPARABLE_LISTINGS_ENABLED)}
            onListingCardClick={handlers.onSimilarListingsCardClick}
          />
        </section>
      }
    />
  ) : null;

  return (
    <>
      {/* Breadcrumbs (always shown if enabled) */}
      {isBreadcrumbsSectionVisible && (
        <ListingHeaderSection sectionId="listing-header" data={headerData} />
      )}

      {/* Header section: Visibility manager controls whether we show sticky nav or action buttons */}
      {visibility.shouldShow(ExperimentElement.STICKY_HEADER_TABS) && (
        <StickyNavWithActions
          sections={listingSections}
          actions={headerData.actions}
          onTabClick={handlers.onStickyHeaderTabClick}
          viewport="desktop"
        />
      )}
      
      {visibility.shouldShow(ExperimentElement.HERO_SHARE_BUTTON) && (
        <ListingActionButtonsSection
          sectionId="listing-actions-buttons"
          actions={headerData.actions}
          show="desktop"
        />
      )}

      {/* Content Layout - LISTING_HERO Experiment */}
      {/* Gallery placement differs: Legacy = full-width, V2 = max-width container */}
      {visibility.shouldShow(ExperimentElement.SIDEBAR_CONNECTION_REQUEST) ? (
        <TwoColumnLayout
          fullWidthContent={heroGallery}
          mainContent={mainContent}
          sidebar={
            <SidebarSection
              data={commonData}
              handlers={eventHandlers}
              connectionRequestSlot={connectionRequestCtaSlot}
            />
          }
          stickySidebar
        />
      ) : (
        defaultHeroLayout
      )}

      {similarListingsSection}

      <section>
        <InternalLinks 
          isActive={hasFeatureFlag(flags, FEATURE_FLAGS.INTERNAL_LINKS_ACTIVE)} 
          data={internalLinks} 
          heading="" 
        />
      </section>
    </>
  );
}
